import React, { useEffect, useState } from 'react';
import { getParts, createPart, updatePart, deletePart, adjustStock } from '../api/client';
import { usePermissions } from '../contexts/PermissionContext';
import { Plus, Search, AlertTriangle, Package } from 'lucide-react';

const CATEGORIES = ['Compressor', 'Filter', 'Thermostat', 'Fan Motor', 'Capacitor', 'Contactor', 'Refrigerant', 'Gasket', 'Evaporator', 'Condenser', 'Control Board', 'Other'];

export default function Parts() {
  const { hasPermission } = usePermissions();
  const canCreate      = hasPermission('parts:create');
  const canEdit        = hasPermission('parts:edit');
  const canAdjustStock = hasPermission('parts:stock:adjust');

  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', part_number: '', category: '', brand: '', description: '', quantity_in_stock: 0, minimum_stock: 5, unit_cost: 0, selling_price: 0, location: '' });

  const load = () => {
    setLoading(true);
    getParts({ search: search || undefined, low_stock: lowStockOnly || undefined }).then(r => setParts(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [search, lowStockOnly]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = { ...form, quantity_in_stock: parseInt(form.quantity_in_stock), minimum_stock: parseInt(form.minimum_stock), unit_cost: parseFloat(form.unit_cost), selling_price: parseFloat(form.selling_price) };
    if (selected) await updatePart(selected.id, data);
    else await createPart(data);
    setShowForm(false);
    setSelected(null);
    load();
  };

  const handleAdjust = async (id, adj) => {
    await adjustStock(id, adj);
    load();
  };

  const totalValue = parts.reduce((sum, p) => sum + p.quantity_in_stock * p.unit_cost, 0);
  const lowStockCount = parts.filter(p => p.quantity_in_stock <= p.minimum_stock).length;

  // Actions column is shown when at least one action is available
  const showActionsCol = canEdit || canAdjustStock;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: '#111827' }}>Parts Inventory</h1>
        {canCreate && (
          <button onClick={() => { setSelected(null); setForm({ name: '', part_number: '', category: '', brand: '', description: '', quantity_in_stock: 0, minimum_stock: 5, unit_cost: 0, selling_price: 0, location: '' }); setShowForm(true); }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
            <Plus size={16} /> Add Part
          </button>
        )}
      </div>

      {/* Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 20 }}>
        {[
          { label: 'Total Parts', value: parts.length, color: '#2563EB', icon: Package },
          { label: 'Low Stock Alerts', value: lowStockCount, color: '#EF4444', icon: AlertTriangle },
          { label: 'Inventory Value', value: `$${totalValue.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, color: '#10B981', icon: Package },
        ].map(({ label, value, color, icon: Icon }) => (
          <div key={label} style={{ background: '#fff', borderRadius: 10, padding: 16, border: '1px solid #F3F4F6', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <p style={{ color: '#6B7280', fontSize: 12, marginBottom: 4 }}>{label}</p>
            <p style={{ fontSize: 22, fontWeight: 700, color }}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search parts..."
            style={{ width: '100%', padding: '8px 12px 8px 30px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13 }} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
          <input type="checkbox" checked={lowStockOnly} onChange={e => setLowStockOnly(e.target.checked)} />
          Low stock only
        </label>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              {['Part #', 'Name', 'Category', 'Brand', 'Stock', 'Min', 'Cost', 'Price', 'Location', ...(showActionsCol ? ['Actions'] : [])].map(h => (
                <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#6B7280', borderBottom: '1px solid #E5E7EB' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={showActionsCol ? 10 : 9} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Loading...</td></tr>
            ) : parts.length === 0 ? (
              <tr><td colSpan={showActionsCol ? 10 : 9} style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>No parts found</td></tr>
            ) : parts.map(part => {
              const isLow = part.quantity_in_stock <= part.minimum_stock;
              return (
                <tr key={part.id} style={{ borderBottom: '1px solid #F9FAFB', background: isLow ? '#FEF2F2' : '#fff' }}>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: '#6B7280', fontFamily: 'monospace' }}>{part.part_number || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{part.name}</div>
                    {part.description && <div style={{ fontSize: 11, color: '#9CA3AF' }}>{part.description.slice(0, 40)}</div>}
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 12 }}>{part.category || '—'}</td>
                  <td style={{ padding: '10px 14px', fontSize: 12 }}>{part.brand || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {canAdjustStock && (
                        <button onClick={() => handleAdjust(part.id, -1)} style={{ width: 22, height: 22, borderRadius: 4, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontWeight: 700 }}>−</button>
                      )}
                      <span style={{ fontWeight: 700, color: isLow ? '#DC2626' : '#111827', minWidth: 24, textAlign: 'center' }}>{part.quantity_in_stock}</span>
                      {canAdjustStock && (
                        <button onClick={() => handleAdjust(part.id, 1)} style={{ width: 22, height: 22, borderRadius: 4, border: '1px solid #D1D5DB', background: '#fff', cursor: 'pointer', fontWeight: 700 }}>+</button>
                      )}
                      {isLow && <AlertTriangle size={13} color="#EF4444" />}
                    </div>
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: '#6B7280' }}>{part.minimum_stock}</td>
                  <td style={{ padding: '10px 14px', fontSize: 12 }}>${part.unit_cost.toFixed(2)}</td>
                  <td style={{ padding: '10px 14px', fontSize: 12, fontWeight: 600, color: '#059669' }}>${part.selling_price.toFixed(2)}</td>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: '#6B7280' }}>{part.location || '—'}</td>
                  {showActionsCol && (
                    <td style={{ padding: '10px 14px' }}>
                      {canEdit && (
                        <button onClick={() => { setSelected(part); setForm({ ...part }); setShowForm(true); }}
                          style={{ padding: '4px 10px', background: '#EFF6FF', color: '#2563EB', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          Edit
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (canCreate || canEdit) && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>{selected ? 'Edit' : 'Add'} Part</h2>
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                {[
                  { label: 'Part Name *', key: 'name', required: true, span: 2 },
                  { label: 'Part Number', key: 'part_number' },
                  { label: 'Brand', key: 'brand' },
                  { label: 'Stock Quantity', key: 'quantity_in_stock', type: 'number' },
                  { label: 'Minimum Stock', key: 'minimum_stock', type: 'number' },
                  { label: 'Unit Cost ($)', key: 'unit_cost', type: 'number', step: '0.01' },
                  { label: 'Selling Price ($)', key: 'selling_price', type: 'number', step: '0.01' },
                  { label: 'Location', key: 'location' },
                ].map(({ label, key, type = 'text', required, step, span }) => (
                  <div key={key} style={{ marginBottom: 14, gridColumn: span === 2 ? '1 / -1' : 'auto' }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>{label}</label>
                    <input type={type} step={step} required={required} value={form[key] || ''} onChange={e => setForm({ ...form, [key]: e.target.value })}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 7, fontSize: 13 }} />
                  </div>
                ))}
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Category</label>
                  <select value={form.category || ''} onChange={e => setForm({ ...form, category: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 7, fontSize: 13 }}>
                    <option value="">Select</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" onClick={() => { setShowForm(false); setSelected(null); }}
                  style={{ padding: '10px 20px', border: '1px solid #D1D5DB', borderRadius: 8, background: '#fff', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
                <button type="submit" style={{ padding: '10px 20px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                  {selected ? 'Update' : 'Add Part'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
